import type { DashboardSaveV2Request, DashboardSaveV2Response } from "@credit-acceptance/step6-bff-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { dscWorkbenchV2Api } from "../../../api/client";
import { generateIdempotencyKey } from "../../../lib/idempotencyKey";
import { useForceExitStore } from "../../../stores/forceExitStore";
import { worknumberKeys } from "../sections/Signers/api/worknumberKeys";

export interface SaveWorkbenchInput {
  approvalId: string;
  payload: DashboardSaveV2Request;
}

export interface SaveWorkbenchResult {
  /**
   * The full v2 save response — includes the write-leg outcomes AND a
   * freshly refreshed full Dashboard snapshot, so callers can hydrate the
   * Dashboard query cache directly instead of issuing a follow-up GET.
   */
  response: DashboardSaveV2Response;
  /**
   * Raw HTTP status of the save response — any 2xx resolves here (axios
   * only rejects on non-2xx), but the footer's success toast should only
   * fire for an exact 200, so callers need the real code, not just success.
   */
  status: number;
}

/**
 * Mutation hook backing the Dashboard-shaped v2 save endpoint (PUT
 * /api/v2/workbench/applications/{approvalId}/Dashboard). Every registered
 * section (Signers today; more to come) contributes its own leg of `payload`
 * via the save orchestrator (see useSaveAllSections); this hook only owns
 * what's common to every save: the force-exit guard, the per-attempt
 * `Idempotency-Key`, and the Work Number cache invalidation: a just-saved
 * Income Type change (e.g. switching to Employee/W2) needs a fresh
 * eligibility read rather than the pre-save `canPull: false` still sitting in
 * cache (OR-23450). Unlike the retired v1 aggregate save, the post-commit
 * stipulation recalc is now requested/returned in this SAME call (see
 * `stipulationActionsResult` on the response) rather than a separate
 * follow-up request.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSaveWorkbench() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ approvalId, payload }: SaveWorkbenchInput): Promise<SaveWorkbenchResult> => {
      // Read live store state (not a subscribed hook value) so a force-exit
      // flag set moments earlier by ReviewPage's click-time recheck is seen
      // even though this component hasn't re-rendered yet.
      if (useForceExitStore.getState().shouldForceExit) {
        throw new Error(t("workbench.saveAbortedForceExit"));
      }

      const idempotencyKey = generateIdempotencyKey();
      const response = await dscWorkbenchV2Api.saveDashboard(approvalId, payload, idempotencyKey);

      return { response: response.data, status: response.status };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: worknumberKeys.all });
    },
  });
}
